import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const defaultContactInfo = {
  phone: '+90 (232) 364 69 83',
  email: 'teslaprotr@gmail.com',
  address: 'Alaybey, 1690 ARABACILAR Sok. No:130/A, 35560 Karşıyaka/İzmir',
  working_hours: 'Pazartesi - Cumartesi: 10:30 - 19:30'
};

const subjects = [
  { value: 'general', label: 'Genel Bilgi' },
  { value: 'service', label: 'Servis Hakkında' },
  { value: 'price', label: 'Fiyat Bilgisi' },
  { value: 'appointment', label: 'Randevu' },
  { value: 'complaint', label: 'Şikayet' },
  { value: 'suggestion', label: 'Öneri' },
  { value: 'other', label: 'Diğer' },
];

const ContactPage = () => {
  const [contactData, setContactData] = useState(defaultContactInfo);
  const [seo, setSeo] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [contactRes, seoRes] = await Promise.all([
          fetch(`${API_URL}/api/content/contact`),
          fetch(`${API_URL}/api/content/seo`)
        ]);
        if (contactRes.ok) {
          const data = await contactRes.json();
          if (data && Object.keys(data).length > 0) {
            setContactData(prev => ({ ...prev, ...data }));
          }
        }
        if (seoRes.ok) {
          const data = await seoRes.json();
          if (data) setSeo(data);
        }
      } catch (e) { console.error(e); }
    };
    fetchContent();
  }, []);

  const contactInfoItems = [
    {
      icon: MapPin,
      title: 'Adres',
      content: [contactData.address],
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: [contactData.phone],
      link: `tel:${contactData.phone.replace(/[^0-9+]/g, '')}`,
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: [contactData.email],
      link: `mailto:${contactData.email}`,
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: contactData.working_hours ? contactData.working_hours.split('\n') : ['Pazartesi - Cumartesi: 10:30 - 19:30'],
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.subject || !formData.message) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSubmitting(true);
    
    // Get subject label
    const subjectLabel = subjects.find(s => s.value === formData.subject)?.label || formData.subject;
    
    // Build WhatsApp message
    const whatsappMessage = `Merhaba, web sitesinden iletişim formu:

*İsim:* ${formData.name}
*Telefon:* ${formData.phone || 'Belirtilmedi'}
*E-posta:* ${formData.email}
*Konu:* ${subjectLabel}

*Mesaj:*
${formData.message}`;

    // Open WhatsApp
    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/902323646983?text=${encodedMessage}`, '_blank');
    
    toast.success('WhatsApp\'a yönlendiriliyorsunuz!', {
      description: 'Mesajınız WhatsApp üzerinden iletilecek.',
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {seo.title && (
        <Helmet>
          <title>{seo.title}</title>
          {seo.description && <meta name="description" content={seo.description} />}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        </Helmet>
      )}
      
      <main className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3"
            >
              İletişim
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-4"
            >
              Bize Ulaşın
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              Sorularınız veya önerileriniz için bizimle iletişime geçebilirsiniz.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {contactInfoItems.map((info) => (
                <div key={info.title} className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{info.title}</h3>
                    {info.content.map((line, index) => (
                      <p key={index} className="text-muted-foreground text-sm">
                        {info.link && index === 0 ? (
                          <a
                            href={info.link}
                            className="hover:text-primary transition-colors"
                          >
                            {line}
                          </a>
                        ) : (
                          line
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              {/* Map */}
              <div className="mt-8">
                <div className="aspect-video rounded-xl overflow-hidden border">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7431.130710578788!2d27.1166864728466!3d38.45519673945233!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14bbd98b2e8ae4b1%3A0x1119cfc38f2ce2b4!2sTeslagen%20Cep%20Telefonu%20l%20Bilgisayar%20l%20Tablet%20Tamiri!5e0!3m2!1str!2str!4v1769613757653!5m2!1str!2str" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="TeslaPro Konum"
                  />
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2"
            >
              <div className="bg-card rounded-2xl border shadow-card p-6 lg:p-8">
                <h2 className="text-2xl font-bold font-display text-foreground mb-6">
                  İletişim Formu
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">İsim Soyisim *</Label>
                      <Input
                        id="name"
                        placeholder="İsim Soyisim"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-posta</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="ornek@email.com"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="05XX XXX XX XX"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Konu *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) =>
                          setFormData({ ...formData, subject: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Konu seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {subjects.map((subject) => (
                            <SelectItem key={subject.value} value={subject.value}>
                              {subject.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mesajınız *</Label>
                    <Textarea
                      id="message"
                      placeholder="Mesajınızı buraya yazın..."
                      rows={6}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" disabled={isSubmitting} className="bg-green-600 hover:bg-green-700">
                    {isSubmitting ? (
                      'Yönlendiriliyor...'
                    ) : (
                      <>
                        WhatsApp ile Gönder
                        <MessageCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;
